import { useState } from "react";
import axios from "axios";
import { Lock, Eye, EyeOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * Self-service change-password card. Lives on the Admin Settings tab but uses
 * `/api/me/password` so it works for any authenticated user.
 */
export default function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!current || !next) { toast.error("Fill both password fields"); return; }
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("New password and confirmation don't match"); return; }
    if (next === current) { toast.error("New password must be different from current"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/me/password`, { current_password: current, new_password: next });
      toast.success("Password changed — use your new password next time you sign in");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-[#121212] border-white/5" data-testid="change-password-card">
      <CardHeader>
        <CardTitle className="text-base font-medium text-white flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#4D88FF]" />
          Change Password
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-zinc-400 text-xs">Current Password</Label>
          <div className="relative">
            <Input
              type={show ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="bg-zinc-900 border-white/10 text-white mt-1 pr-10"
              data-testid="cp-current"
              autoComplete="current-password"
            />
            <button
              onClick={() => setShow((s) => !s)}
              type="button"
              className="absolute right-2 top-3 text-white/40 hover:text-white"
              tabIndex={-1}
              data-testid="cp-toggle-show"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-zinc-400 text-xs">New Password</Label>
            <Input
              type={show ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="bg-zinc-900 border-white/10 text-white mt-1"
              data-testid="cp-new"
              autoComplete="new-password"
            />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Confirm New</Label>
            <Input
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="bg-zinc-900 border-white/10 text-white mt-1"
              data-testid="cp-confirm"
              autoComplete="new-password"
            />
          </div>
        </div>
        <p className="text-[11px] text-white/40">
          Minimum 8 characters. After saving, you'll stay signed in here but will need the new password next time.
        </p>
        <Button
          onClick={submit}
          disabled={busy}
          className="w-full bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold"
          data-testid="cp-submit"
        >
          {busy ? "Updating…" : "Update password"}
        </Button>
      </CardContent>
    </Card>
  );
}
